import { QUIZ_IMAGE_BASE_URL } from "../../config/paymentUrls";

const DEFAULT_IMAGE_HOST = QUIZ_IMAGE_BASE_URL;
const FACEMESH_SOURCE_PROXY_PATH = "/api/facemesh/source";
const FACEMESH_V2_OVERLAY_PATH = "/api/facemesh-v2/overlay";

export const unwrapImageParam = (rawValue) => {
  if (!rawValue) return "";

  const trimmed = String(rawValue).trim();
  if (!trimmed) return "";

  let decoded = trimmed;
  if (/%[0-9a-f]{2}/i.test(trimmed)) {
    try {
      decoded = decodeURIComponent(trimmed);
    } catch (_) {
      decoded = trimmed;
    }
  }

  if (!/[<>]/.test(decoded) && !/href\s*=/.test(decoded)) {
    return decoded;
  }

  try {
    const doc = new DOMParser().parseFromString(decoded, "text/html");
    const href = doc.querySelector("a[href]")?.getAttribute("href")?.trim();
    if (href) return href;
  } catch (_) {}

  const hrefMatch = decoded.match(/href\s*=\s*["']([^"']+)["']/i);
  if (hrefMatch?.[1]) return hrefMatch[1].trim();

  return decoded;
};

export const normalizeImageUrl = (rawUrl) => {
  if (!rawUrl) return "";
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  if (rawUrl.startsWith("//")) return `https:${rawUrl}`;
  if (rawUrl.startsWith("/")) return `${DEFAULT_IMAGE_HOST}${rawUrl}`;
  return `${DEFAULT_IMAGE_HOST}/${rawUrl}`;
};

export const getOriginalPhotoPreviewUrl = (rawImageValue, apiBase = "") => {
  const normalizedUrl = normalizeImageUrl(unwrapImageParam(rawImageValue));
  if (!normalizedUrl) return "";

  try {
    const parsedUrl = new URL(normalizedUrl);
    if (parsedUrl.hostname === "quiz.color-analysis.pro") {
      return `${apiBase}${FACEMESH_SOURCE_PROXY_PATH}?url=${encodeURIComponent(parsedUrl.toString())}`;
    }
    return parsedUrl.toString();
  } catch (_) {
    return normalizedUrl;
  }
};

export const getFacemeshOverlayPreviewUrl = (jobId, apiBase = "") => {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) return "";
  return `${apiBase}${FACEMESH_V2_OVERLAY_PATH}/${encodeURIComponent(normalizedJobId)}`;
};

export const getExplicitFacemeshOverlayPreviewUrl = (rawOverlayUrl) => {
  const normalizedOverlayUrl = String(rawOverlayUrl || "").trim();
  if (!normalizedOverlayUrl) return "";

  try {
    const parsedUrl = new URL(normalizedOverlayUrl);
    if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
      return parsedUrl.toString();
    }
  } catch (_) {}

  return "";
};

export const getUserPhotoHeroSources = (search = "", apiBase = "") => {
  return getUserPhotoHeroSourceGroups(search, apiBase).orderedSources;
};

export const getUserPhotoHeroSourceGroups = (search = "", apiBase = "") => {
  const params = new URLSearchParams(search || "");
  const rawImageValue = params.get("img") || params.get("image") || "";
  const facemeshJobId = params.get("facemesh_job_id") || "";
  const explicitFacemeshOverlayUrl = params.get("facemesh_overlay_url") || "";
  // Explicit quiz URLs belong to the quiz's storage, not the payment host.
  const overlaySources = [
    getExplicitFacemeshOverlayPreviewUrl(explicitFacemeshOverlayUrl) ||
      getFacemeshOverlayPreviewUrl(facemeshJobId, apiBase),
  ].filter(Boolean);
  const originalPhotoSource = getOriginalPhotoPreviewUrl(rawImageValue, apiBase);
  const orderedSources = [
    ...overlaySources,
    originalPhotoSource,
  ].filter(Boolean);

  return {
    overlaySources,
    originalPhotoSource,
    orderedSources,
  };
};
